import axios from 'axios';

/**
 * Custom data provider for users resource that makes direct REST API calls
 * to /api/v1/users endpoints instead of using Hasura GraphQL.
 */
export const usersDataProvider = {
    getList: async (resource, params) => {
        if (resource !== 'users') {
            throw new Error(`Unsupported resource: ${resource}`);
        }

        try {
            // Get token and check if it exists
            const token = localStorage.getItem('token');
            if (!token) {
                console.error('No auth token found in localStorage');
                throw new Error('Authentication token is missing');
            }
            const apiUrl = window.REACT_APP_API_URL || '';

            // Extract filter and pagination params
            const { page, perPage } = params.pagination || {};
            const { field, order } = params.sort || {};
            const { name } = params.filter || {};

            // Use client-side filtering for search like Hasura does
            // This ensures search works immediately without backend changes
            const url = `${apiUrl}/api/v1/users`;

            const response = await axios.get(
                url,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                    },
                }
            );

            // Map the response to the format expected by react-admin
            // API returns { users: [...], total: N }
            let users = response.data.users || [];

            // Client-side filtering for search (like Hasura does)
            if (name) {
                users = users.filter(user =>
                    user.name?.toLowerCase().includes(name.toLowerCase()) ||
                    user.email?.toLowerCase().includes(name.toLowerCase())
                );
            }

            // Client-side sorting
            if (field && order) {
                users.sort((a, b) => {
                    const aVal = a[field] || '';
                    const bVal = b[field] || '';
                    if (order === 'ASC') {
                        return aVal.localeCompare(bVal);
                    } else {
                        return bVal.localeCompare(aVal);
                    }
                });
            }

            // Client-side pagination
            const total = users.length;
            if (page && perPage) {
                const start = (page - 1) * perPage;
                const end = start + perPage;
                users = users.slice(start, end);
            }
            return {
                data: users,
                total: total,
            };
        } catch (error) {
            console.error('Error fetching users:', error);
            throw error;
        }
    },

    getOne: async (resource, params) => {
        if (resource !== 'users') {
            throw new Error(`Unsupported resource: ${resource}`);
        }

        try {
            // Get token and check if it exists
            const token = localStorage.getItem('token');
            if (!token) {
                console.error('No auth token found in localStorage');
                throw new Error('Authentication token is missing');
            }

            const apiUrl = window.REACT_APP_API_URL || '';

            const response = await axios.get(
                `${apiUrl}/api/v1/users/${params.id}`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                    },
                }
            );

            // Transform response data to include a single role property for the form
            return {
                data: {
                    ...response.data,
                    // Convert roles array to single role for form display
                    role: response.data.roles && response.data.roles.length > 0
                        ? response.data.roles[0]
                        : ''
                },
            };
        } catch (error) {
            console.error('Error fetching user:', error);
            throw error;
        }
    },

    getMany: async (resource, params) => {
        if (resource !== 'users') {
            throw new Error(`Unsupported resource: ${resource}`);
        }

        try {
            // Get token and check if it exists
            const token = localStorage.getItem('token');
            if (!token) {
                console.error('No auth token found in localStorage');
                throw new Error('Authentication token is missing');
            }

            const apiUrl = window.REACT_APP_API_URL || '';

            // This could be optimized with a batch endpoint if available
            const responses = await Promise.all(
                params.ids.map(id =>
                    axios.get(
                        `${apiUrl}/api/v1/users/${id}`,
                        {
                            headers: {
                                'Authorization': `Bearer ${token}`,
                            },
                        }
                    )
                )
            );

            return {
                data: responses.map(response => response.data),
            };
        } catch (error) {
            console.error('Error fetching users:', error);
            throw error;
        }
    },

    create: async (resource, params) => {
        if (resource !== 'users') {
            throw new Error(`Unsupported resource: ${resource}`);
        }

        try {
            // Get token and check if it exists
            const token = localStorage.getItem('token');
            if (!token) {
                console.error('No auth token found in localStorage');
                throw new Error('Authentication token is missing');
            }

            const apiUrl = window.REACT_APP_API_URL || '';

            // Transform data to match API expectations
            // Convert single role to an array
            const payload = {
                ...params.data,
                roles: params.data.role ? [params.data.role] : []
            };

            // Remove role field as the backend expects roles array
            delete payload.role;

            const response = await axios.post(
                `${apiUrl}/api/v1/users`,
                payload,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            // Return the created user with its ID
            return {
                data: {
                    ...response.data,
                    id: response.data.id,
                    // Convert roles array back to single role for form display
                    role: response.data.roles && response.data.roles.length > 0
                        ? response.data.roles[0]
                        : ''
                },
            };
        } catch (error) {
            console.error('Error creating user:', error);
            throw error;
        }
    },

    update: async (resource, params) => {
        if (resource !== 'users') {
            throw new Error(`Unsupported resource: ${resource}`);
        }

        try {
            // Get token and check if it exists
            const token = localStorage.getItem('token');
            if (!token) {
                console.error('No auth token found in localStorage');
                throw new Error('Authentication token is missing');
            }

            const apiUrl = window.REACT_APP_API_URL || '';

            // Create a clean payload with ONLY the required fields
            const payload = {
                name: params.data.name,
                roles: params.data.role ? [params.data.role] : []
            };

            // Include password if it exists (simplified approach like create)
            if (params.data.password) {
                payload.password = params.data.password;
            }

            const response = await axios.put(
                `${apiUrl}/api/v1/users/${params.id}`,
                payload,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            // Return the updated user
            return {
                data: {
                    ...response.data,
                    id: response.data.id,
                    // Convert roles array back to single role for form display
                    role: response.data.roles && response.data.roles.length > 0
                        ? response.data.roles[0]
                        : ''
                },
            };
        } catch (error) {
            console.error('Error updating user:', error);
            throw error;
        }
    },

    delete: async (resource, params) => {
        if (resource !== 'users') {
            throw new Error(`Unsupported resource: ${resource}`);
        }

        try {
            // Get token and check if it exists
            const token = localStorage.getItem('token');
            if (!token) {
                console.error('No auth token found in localStorage');
                throw new Error('Authentication token is missing');
            }
            const apiUrl = window.REACT_APP_API_URL || '';

            await axios.delete(
                `${apiUrl}/api/v1/users/${params.id}`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                    }
                }
            );

            return { data: { id: params.id } };
        } catch (error) {
            console.error('Error deleting user:', error);
            throw error;
        }
    },

    deleteMany: async (resource, params) => {
        if (resource !== 'users') {
            throw new Error(`Unsupported resource: ${resource}`);
        }

        try {
            // Get token and check if it exists
            const token = localStorage.getItem('token');
            if (!token) {
                console.error('No auth token found in localStorage');
                throw new Error('Authentication token is missing');
            }

            const apiUrl = window.REACT_APP_API_URL || '';

            await Promise.all(
                params.ids.map(id =>
                    axios.delete(
                        `${apiUrl}/api/v1/users/${id}`,
                        {
                            headers: {
                                'Authorization': `Bearer ${token}`,
                            }
                        }
                    )
                )
            );

            return { data: params.ids };
        } catch (error) {
            console.error('Error deleting users:', error);
            throw error;
        }
    },

    // These methods are less commonly used but included for completeness
    getManyReference: async (resource, params) => {
        // Implementation would be similar to getList
        // but with a filter based on params.target and params.id
        return usersDataProvider.getList(resource, {
            ...params,
            filter: {
                ...params.filter,
                [params.target]: params.id,
            },
        });
    },

    updateMany: async (resource, params) => {
        // Update all records and wait for completion
        await Promise.all(
            params.ids.map(id =>
                usersDataProvider.update(resource, {
                    id,
                    data: params.data,
                })
            )
        );

        return { data: params.ids };
    },
};

export default usersDataProvider;
